import { create } from 'zustand';
import { supabase } from '@/lib/supabase/client';
import type { Note, NoteFormData } from '@/types/crm';

interface NoteStore {
  notes: Note[];
  isLoading: boolean;
  fetchNotesByLead: (leadId: string) => Promise<Note[]>;
  fetchNotesByDeal: (dealId: string) => Promise<Note[]>;
  createNote: (data: NoteFormData) => Promise<Note | null>;
  updateNote: (id: string, content: string) => Promise<void>;
  deleteNote: (id: string) => Promise<void>;
}

export const useNoteStore = create<NoteStore>((set, get) => ({
  notes: [],
  isLoading: false,

  fetchNotesByLead: async (leadId: string) => {
    set({ isLoading: true });
    try {
      const { data, error } = await supabase
        .from('crm_notes')
        .select('*')
        .eq('lead_id', leadId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      set({ notes: data || [] });
      return data || [];
    } finally {
      set({ isLoading: false });
    }
  },

  fetchNotesByDeal: async (dealId: string) => {
    set({ isLoading: true });
    try {
      const { data, error } = await supabase
        .from('crm_notes')
        .select('*')
        .eq('deal_id', dealId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      set({ notes: data || [] });
      return data || [];
    } finally {
      set({ isLoading: false });
    }
  },

  createNote: async (data: NoteFormData) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data: note, error } = await supabase
      .from('crm_notes')
      .insert({
        ...data,
        created_by: user.id,
      })
      .select()
      .single();

    if (error) throw error;
    if (data.lead_id) {
      await get().fetchNotesByLead(data.lead_id);
    } else if (data.deal_id) {
      await get().fetchNotesByDeal(data.deal_id);
    }
    return note;
  },

  updateNote: async (id: string, content: string) => {
    const { error } = await supabase
      .from('crm_notes')
      .update({ content, updated_at: new Date().toISOString() })
      .eq('id', id);

    if (error) throw error;
    
    const { notes } = get();
    if (notes.length > 0 && notes[0].lead_id) {
      await get().fetchNotesByLead(notes[0].lead_id);
    }
  },

  deleteNote: async (id: string) => {
    const { notes } = get();
    const note = notes.find(n => n.id === id);
    
    const { error } = await supabase
      .from('crm_notes')
      .delete()
      .eq('id', id);

    if (error) throw error;
    
    if (note?.lead_id) {
      await get().fetchNotesByLead(note.lead_id);
    }
  },
}));
